import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL, { getAzureAdAuthorizeUrl, getPkceVerifier } from '../config';
import '../assets/css/style.css';

function Login({ setAuth }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [azureLoading, setAzureLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(function() {
    var azureError = sessionStorage.getItem('__azure_error__');
    if (azureError) {
      sessionStorage.removeItem('__azure_error__');
      setError(azureError);
      return;
    }

    // NEW: PKCE code exchange flow — capture ?code= from URL after Microsoft redirect
    var urlParams = new URLSearchParams(window.location.search);
    var authCode = urlParams.get('code');
    if (authCode) {
      // Remove code from URL so it doesn't linger
      window.history.replaceState({}, document.title, window.location.pathname);
      handleAzureExchange(authCode);
      return;
    }

    // OLD: implicit flow fallback via stored id_token
    var idToken = sessionStorage.getItem('__msal_idtoken__');
    if (idToken) {
      sessionStorage.removeItem('__msal_idtoken__');
      handleAzureLoginLegacy(idToken);
    }
  }, []);

  /** NEW: PKCE code exchange flow (uses /azure-exchange) — primary flow for web interface */
  function handleAzureExchange(code) {
    setAzureLoading(true);
    setError('');

    var codeVerifier = getPkceVerifier();
    if (!codeVerifier) {
      setError('Session expired. Please try signing in again.');
      setAzureLoading(false);
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('POST', API_URL + '/webauth/azure-exchange', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onreadystatechange = function() {
      if (xhr.readyState !== 4) return;
      if (xhr.status === 200) {
        var data = JSON.parse(xhr.responseText);
        if (data.authenticated) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user_role', data.role);
          localStorage.setItem('user_id', data.user_id);
          localStorage.setItem('username', data.username);
          localStorage.setItem('full_name', data.full_name || data.username);
          localStorage.setItem('user_divisions', JSON.stringify(data.divisions || []));
          localStorage.setItem('user_countries', JSON.stringify(data.countries || []));
          localStorage.setItem('user_permissions', JSON.stringify(data.permissions || []));
          if (data.must_change_password) {
            localStorage.setItem('must_change_password', 'true');
            window.location.href = '/change-password';
          } else {
            localStorage.removeItem('must_change_password');
            window.location.href = '/';
          }
          return;
        }
        setError(data.error || 'User not registered');
      } else {
        try { setError(JSON.parse(xhr.responseText).error); } catch(e) { setError('Auth failed (HTTP ' + xhr.status + ')'); }
      }
      setAzureLoading(false);
    };
    xhr.send(JSON.stringify({
      code: code,
      code_verifier: codeVerifier,
      redirect_uri: window.location.origin + '/'
    }));
  }

  /** OLD: implicit flow fallback (uses /azure-login) */
  function handleAzureLoginLegacy(idToken) {
    setAzureLoading(true);
    setError('');

    var xhr = new XMLHttpRequest();
    xhr.open('POST', API_URL + '/webauth/azure-login', true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onreadystatechange = function() {
      if (xhr.readyState !== 4) return;
      if (xhr.status === 200) {
        var data = JSON.parse(xhr.responseText);
        if (data.authenticated) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user_role', data.role);
          localStorage.setItem('user_id', data.user_id);
          localStorage.setItem('username', data.username);
          localStorage.setItem('full_name', data.full_name || data.username);
          localStorage.setItem('user_divisions', JSON.stringify(data.divisions || []));
          localStorage.setItem('user_countries', JSON.stringify(data.countries || []));
          localStorage.setItem('user_permissions', JSON.stringify(data.permissions || []));
          if (data.must_change_password) {
            localStorage.setItem('must_change_password', 'true');
            window.location.href = '/change-password';
          } else {
            localStorage.removeItem('must_change_password');
            window.location.href = '/';
          }
          return;
        }
        setError(data.error || 'User not registered');
      } else {
        try { setError(JSON.parse(xhr.responseText).error); } catch(e) { setError('Auth failed (HTTP ' + xhr.status + ')'); }
      }
      setAzureLoading(false);
    };
    xhr.send(JSON.stringify({ id_token: idToken }));
  }

  function handleMicrosoftLogin() {
    window.location.href = getAzureAdAuthorizeUrl();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var res = await axios.post(API_URL + '/webauth/login', { username, password });
      localStorage.setItem('user_role', res.data.role);
      localStorage.setItem('user_id', res.data.user_id);
      localStorage.setItem('username', res.data.username);
      localStorage.setItem('full_name', res.data.full_name || res.data.username);
      localStorage.setItem('user_divisions', JSON.stringify(res.data.divisions || []));
      localStorage.setItem('user_countries', JSON.stringify(res.data.countries || []));
      localStorage.setItem('user_permissions', JSON.stringify(res.data.permissions || []));
      if (res.data.must_change_password) {
        localStorage.setItem('must_change_password', 'true');
        setAuth(res.data.token);
        navigate('/change-password');
      } else {
        localStorage.removeItem('must_change_password');
        setAuth(res.data.token);
        navigate('/');
      }
    } catch (err) { setError('Invalid credentials'); }
  }

  return (
    <div className="login-wrapper">
      <div className="login-banner">
        <div className="banner-content">
          <div className="brand">
            <h1 className="logo-text">ACTS</h1>
            <h2 className="company-name">Advanced Construction Technology Services</h2>
          </div>
          <div className="hero-text">
            <h3>A Legacy of Excellence</h3>
            <p>Since 1996, the regional leader in construction consultancy, materials testing, and geo-engineering.</p>
          </div>
          <div className="pillars">
            <div className="pillar"><i className="fas fa-check-circle"></i> Quality & Safety</div>
            <div className="pillar"><i className="fas fa-flask"></i> Testing & Inspection</div>
            <div className="pillar"><i className="fas fa-hard-hat"></i> Engineering Services</div>
          </div>
        </div>
        <div className="banner-overlay"></div>
      </div>

      <div className="login-form-container">
        <div className="form-wrapper">
          <div className="mobile-logo">ACTS</div>
          <h2>Welcome Back</h2>
          <p className="subtitle">Sign in to access your ACTS applications.</p>

          <button type="button" onClick={handleMicrosoftLogin} disabled={azureLoading}
            style={{ backgroundColor:'#2F2F2F',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',padding:'0.85rem',fontSize:'1rem',marginBottom:'1.5rem',border:'none',borderRadius:'6px',cursor:'pointer',width:'100%' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            {azureLoading ? 'Signing in...' : 'Sign in with Microsoft'}
          </button>

       

          <form hidden={true} id="actsLoginForm" onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="username">Corporate Email or Username</label>
              <div className="input-wrapper">
                <i className="fas fa-envelope input-icon"></i>
                <input type="text" id="username" name="username" placeholder="e.g. j.doe@acts-int.com" autoComplete="username" required value={username} onChange={function(e){setUsername(e.target.value)}} />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <i className="fas fa-lock input-icon"></i>
                <input type={showPassword?'text':'password'} id="password" name="password" placeholder="Enter your password" autoComplete="current-password" required value={password} onChange={function(e){setPassword(e.target.value)}} />
                <button type="button" className="toggle-password" onClick={function(){setShowPassword(!showPassword)}}>
                  <i className={'fas '+(showPassword?'fa-eye-slash':'fa-eye')}></i>
                </button>
              </div>
              {error && <span className="error-msg">{error}</span>}
            </div>

            <div className="form-actions">
              <label className="checkbox-container">
                <input type="checkbox" /><span className="checkmark"></span>Remember me
              </label>
            </div>

            <button type="submit" className="btn btn-primary btn-block" style={{ padding:'1rem',fontSize:'1.05rem' }}>
              <span>Sign In</span><i className="fas fa-arrow-right"></i>
            </button>
          </form>

          <div className="system-notice">
            <p><i className="fas fa-shield-alt"></i> Secure access for authorized ACTS personnel only.<br/>Need help? <a href="#">Contact IT Support</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;